import { Box, HStack } from '@chakra-ui/react';
import {
  useRef,
  useState,
  useEffect,
  createContext,
  useContext,
  type ReactNode,
  type RefObject,
} from 'react';

type CarouselContextType = {
  scrollContainerRef: RefObject<HTMLDivElement>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  needsScrolling: boolean;
  scroll: (direction: 'left' | 'right') => void;
  updateScrollButtons: () => void;
};

const CarouselContext = createContext<CarouselContextType | null>(null);

const defaultCarouselContext: CarouselContextType = {
  scrollContainerRef: { current: null } as RefObject<HTMLDivElement>,
  canScrollLeft: false,
  canScrollRight: false,
  needsScrolling: false,
  scroll: () => undefined,
  updateScrollButtons: () => undefined,
};

let hasWarnedAboutContext = false;

const useCarouselContext = () => {
  const context = useContext(CarouselContext);
  if (!context) {
    if (process.env.NODE_ENV !== 'production' && !hasWarnedAboutContext) {
      // eslint-disable-next-line no-console
      console.warn('Carousel compound components must be used within Carousel');
      hasWarnedAboutContext = true;
    }
    return defaultCarouselContext;
  }
  return context;
};

type CarouselProps = {
  children: ReactNode;
  itemWidth: number;
  gap: number;
  className?: string;
  onDataChange?: (hasData: boolean) => void;
};

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M12.5 15L7.5 10L12.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M7.5 15L12.5 10L7.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CarouselComponent = ({
  children,
  itemWidth,
  gap,
  className,
}: CarouselProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [needsScrolling, setNeedsScrolling] = useState(false);

  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      const hasOverflow = scrollWidth > clientWidth;
      setNeedsScrolling(hasOverflow);
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener('resize', updateScrollButtons);
    return () => window.removeEventListener('resize', updateScrollButtons);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.clientWidth;
      const itemsPerView = Math.floor(containerWidth / (itemWidth + gap));
      const scrollAmount = itemsPerView * (itemWidth + gap);

      const currentScroll = scrollContainerRef.current.scrollLeft;
      const newScrollLeft =
        direction === 'right'
          ? currentScroll + scrollAmount
          : currentScroll - scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  return (
    <CarouselContext.Provider
      value={{
        scrollContainerRef,
        canScrollLeft,
        canScrollRight,
        needsScrolling,
        scroll,
        updateScrollButtons,
      }}
    >
      <Box w="100%" overflow="hidden" className={className}>
        {children}
      </Box>
    </CarouselContext.Provider>
  );
};

type CarouselHeaderProps = {
  children: ReactNode;
};

const CarouselHeader = ({ children }: CarouselHeaderProps) => {
  return (
    <HStack justify="space-between" mb="24px">
      {children}
    </HStack>
  );
};

type CarouselTitleProps = {
  children: ReactNode;
};

const CarouselTitle = ({ children }: CarouselTitleProps) => {
  return (
    <Box as="h2" fontSize="22px" fontWeight="medium" color="textPrimary">
      {children}
    </Box>
  );
};

const CarouselNavigation = () => {
  const { canScrollLeft, canScrollRight, needsScrolling, scroll } =
    useCarouselContext();

  if (!needsScrolling) return null;

  return (
    <HStack gap="8px">
      <Box
        as="button"
        onClick={() => scroll('left')}
        w="32px"
        h="32px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="6px"
        border="1px solid"
        borderColor="bgCompSecondaryMid"
        bg="transparent"
        color="gray"
        cursor={canScrollLeft ? 'pointer' : 'not-allowed'}
        opacity={canScrollLeft ? 1 : 0.5}
        transition="all 0.2s"
        pointerEvents={canScrollLeft ? 'auto' : 'none'}
        css={{
          '&:hover': {
            backgroundColor: canScrollLeft
              ? 'var(--bgCompSecondaryTiny)'
              : 'transparent',
          },
        }}
      >
        <ChevronLeft />
      </Box>
      <Box
        as="button"
        onClick={() => scroll('right')}
        w="32px"
        h="32px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="6px"
        border="1px solid"
        borderColor="bgCompSecondaryMid"
        bg="transparent"
        color="gray"
        cursor={canScrollRight ? 'pointer' : 'not-allowed'}
        opacity={canScrollRight ? 1 : 0.5}
        transition="all 0.2s"
        pointerEvents={canScrollRight ? 'auto' : 'none'}
        css={{
          '&:hover': {
            backgroundColor: canScrollRight
              ? 'var(--bgCompSecondaryTiny)'
              : 'transparent',
          },
        }}
      >
        <ChevronRight />
      </Box>
    </HStack>
  );
};

type CarouselContentProps = {
  children: ReactNode;
  gap: number;
};

const CarouselContent = ({ children, gap }: CarouselContentProps) => {
  const { scrollContainerRef, updateScrollButtons } = useCarouselContext();

  return (
    <Box
      ref={scrollContainerRef}
      overflowX="scroll"
      overflowY="hidden"
      w="100%"
      maxW="100%"
      onScroll={updateScrollButtons}
      css={{
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollSnapType: 'x mandatory',
      }}
    >
      <Box display="inline-flex" gap={`${gap}px`}>
        {children}
      </Box>
    </Box>
  );
};

type CarouselItemProps = {
  children: ReactNode;
};

const CarouselItem = ({ children }: CarouselItemProps) => {
  return (
    <Box flexShrink={0} css={{ scrollSnapAlign: 'start' }}>
      {children}
    </Box>
  );
};

type CarouselCompoundComponent = ((props: CarouselProps) => JSX.Element) & {
  Header: typeof CarouselHeader;
  Title: typeof CarouselTitle;
  Navigation: typeof CarouselNavigation;
  Content: typeof CarouselContent;
  Item: typeof CarouselItem;
};

export const Carousel = Object.assign(CarouselComponent, {
  Header: CarouselHeader,
  Title: CarouselTitle,
  Navigation: CarouselNavigation,
  Content: CarouselContent,
  Item: CarouselItem,
}) as CarouselCompoundComponent;
